// Winner headline + the arithmetic table (Territory / Prisoners / Komi /
// Total per color) straight from ScoreBreakdown.

import { useEffect, useRef } from 'react';
import type { ScoreBreakdown } from '../../shared/types.ts';

export interface ResultModalProps {
  breakdown: ScoreBreakdown;
  onClose(): void;
  onExplain(): void;
  onReview(): void;
  onNewGame(): void;
}

export function ResultModal({ breakdown, onClose, onExplain, onReview, onNewGame }: ResultModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus();
    };
  }, []);

  // A drawn score carries result 'Draw' with winner null — the draw branch
  // below renders before `winner` is ever consulted.
  const isDraw = breakdown.result === 'Draw';
  const winner = breakdown.winner === 'B' ? 'Black' : 'White';
  const margin = isDraw
    ? null
    : breakdown.result.endsWith('+R')
      ? 'by resignation'
      : `by ${Math.abs(breakdown.black.total - breakdown.white.total)} points`;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="result-title"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="result-kicker mono">{breakdown.result}</p>
        <h2 id="result-title" className="result-headline">
          {isDraw ? 'Drawn game' : `${winner} wins ${margin ?? ''}`}
        </h2>

        <table className="score-table">
          <caption className="sr-only">Score breakdown per color</caption>
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col">
                <span className="stone-icon stone-icon-b table-stone" aria-hidden="true" /> Black
              </th>
              <th scope="col">
                <span className="stone-icon stone-icon-w table-stone" aria-hidden="true" /> White
              </th>
            </tr>
          </thead>
          <tbody className="mono">
            <tr>
              <th scope="row">Territory</th>
              <td>{breakdown.black.territory}</td>
              <td>{breakdown.white.territory}</td>
            </tr>
            <tr>
              <th scope="row">Prisoners</th>
              <td>{breakdown.black.prisoners}</td>
              <td>{breakdown.white.prisoners}</td>
            </tr>
            <tr>
              <th scope="row">Komi</th>
              <td>—</td>
              <td>{breakdown.white.komi}</td>
            </tr>
            <tr className="score-total">
              <th scope="row">Total</th>
              <td>{breakdown.black.total}</td>
              <td>{breakdown.white.total}</td>
            </tr>
          </tbody>
        </table>

        <p className="result-footnote">
          {breakdown.deadStones.length > 0
            ? `${breakdown.deadStones.length} dead ${breakdown.deadStones.length === 1 ? 'stone' : 'stones'} counted as prisoners · `
            : ''}
          {breakdown.damePoints.length} dame · Japanese rules
        </p>

        <div className="modal-actions">
          <button type="button" className="btn btn-accent" onClick={onExplain}>
            Explain in chat
          </button>
          <button type="button" className="btn" onClick={onReview}>
            Review game
          </button>
          <button type="button" className="btn" onClick={onNewGame}>
            New game
          </button>
          <button type="button" ref={closeRef} className="btn btn-quiet" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
